import "./media.scss";
import {
    type FC,
    type ReactNode,
    useCallback,
    useRef,
    useState,
} from "react";
import {
    Button,
    ButtonColour,
    ButtonStyle,
    useLayerCreate,
    useLayerRemove,
} from "@onshopfront/components";
import { uppercaseFirst } from "../../Utilities/Generic/Strings";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { MediaLayer } from "./MediaLayer";
import { type MediaSelectorProps } from "./Selector/MediaSelector";
import { type MediaType } from "./Selector/MediaUtilities";

interface Props {
    type: "image" | "video";
    source?: string;
    removable?: boolean;
    noSource: null | ReactNode;
    hideRemove?: boolean;
    filters?: Array<MediaType>;
    onChange: (value: null | string) => void;
    onRemove?: () => void;
    onLoad?: () => void;
    onOpen?: (onChange: (value: null | string) => void) => void;
}

const Media: FC<Props> = ({
    type,
    source,
    removable,
    noSource,
    hideRemove,
    filters,
    onChange: onChangeProps,
    onRemove,
    onLoad,
    onOpen,
}) => {
    const layerRef = useRef<string | null>(null);
    const onChangeRef = useRef<Props["onChange"]>(() => {});

    const [ error, setError ] = useState(false);

    const mediaCreate = useLayerCreate<MediaSelectorProps>(MediaLayer, "media");
    const removeLayer = useLayerRemove();

    const removeMediaLayer = useCallback(() => {
        if(layerRef.current) {
            removeLayer(layerRef.current);
            layerRef.current = null;
        }
    }, [ removeLayer ]);

    const onChange = useCallback((value: string | null) => {
        removeMediaLayer();
        setError(false);
        onChangeProps(value);
    }, [ removeMediaLayer, onChangeProps ]);

    onChangeRef.current = onChange;

    const openMedia = () => {
        if(typeof onOpen === "function") {
            onOpen(onChange);

            return;
        }

        if(layerRef.current) {
            return;
        }

        layerRef.current = mediaCreate({
            onChange: (value) => {
                onChangeRef.current(value[0].url);
            },
            onClose: () => {
                if(layerRef.current) {
                    removeLayer(layerRef.current);
                    layerRef.current = null;
                }
            },
            filters,
        });
    };

    let item: ReactNode;

    if(!source || error) {
        item = noSource;
    } else if(type === "image") {
        item = (
            <img
                src={source}
                alt="Click to select an Image"
                onError={() => {
                    setError(true);
                }}
                onLoad={onLoad}
            />
        );
    } else if(type === "video") {
        item = (
            <video
                src={source}
                onError={() => {
                    setError(true);
                }}
                onCanPlay={onLoad}
            />
        );
    }

    return (
        <div className="media-item-container">
            <div
                className="media-item"
                onClick={openMedia}
            >
                {item}
                <div className="media-overlay">
                    <p>Select to {source ? "Replace" : "Add"} {uppercaseFirst(type)}</p>
                </div>
            </div>
            {removable && !(hideRemove && !source) && (
                <Button
                    type="button"
                    colour={ButtonColour.Neutral}
                    style={ButtonStyle.Outlined}
                    className="media-remove"
                    onClick={() => {
                        setError(false);

                        if(typeof onRemove === "function") {
                            onRemove();
                        }

                        onChangeProps(null);
                    }}
                    disabled={!source && typeof onRemove !== "function"}
                >
                    <FontAwesomeLight icon="times" /> Remove Image
                </Button>
            )}
        </div>
    );
};

export default Media;
